import { useState, useCallback, useRef, useEffect } from 'react';

interface FilterBarTooltipState {
  visible: boolean;
  left: number;
  top: number;
  items: string[];
  rightAligned: boolean;
}

interface UseFilterBarTooltipReturn {
  tooltip: FilterBarTooltipState;
  showTooltip: (left: number, top: number, items: string[], rightAligned?: boolean) => void;
  hideTooltip: () => void;
  cancelHide: () => void;
}

/**
 * Manages the state of the filter bar overflow tooltip.
 * Uses a small hide delay so the user can move the mouse from the indicator to the tooltip
 * without it disappearing.
 */
export const useFilterBarTooltip = (): UseFilterBarTooltipReturn => {
  const [tooltip, setTooltip] = useState<FilterBarTooltipState>({
    visible: false,
    left: 0,
    top: 0,
    items: [],
    rightAligned: false,
  });

  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  const showTooltip = useCallback((left: number, top: number, items: string[], rightAligned = false) => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    setTooltip({ visible: true, left, top, items, rightAligned });
  }, []);

  const hideTooltip = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      setTooltip((prev) => ({ ...prev, visible: false }));
    }, 120);
  }, []);

  const cancelHide = useCallback(() => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
  }, []);

  return { tooltip, showTooltip, hideTooltip, cancelHide };
};
